// @ts-nocheck
import { useEffect, useState, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import NotificationAlert from '../../shared/components/NotificationAlert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { sendPasswordResetEmail } from 'firebase/auth';
import { useAuth } from '../../shared/context/AuthContext';
import * as adminService from '../../services/adminService';
import { getHospital } from '../../services/hospitalService';
import UsersDataGrid from '../../users/components/UsersDataGrid.jsx';
import UserViewDialog from '../../users/components/UserViewDialog.jsx';
import { formatTimestamp, resolveUidToEmail } from '../../shared/utils/metadataUtils';

export default function HospitalViewDialog({ open, onClose, hospital, loading = false }) {
  const { auth } = useAuth();
  const disabledSx = {
    '& .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled': {
      color: 'rgba(0,0,0,0.87)',
    },
    '& .MuiInputBase-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0,0,0,0.23)',
    },
    '& .MuiInputBase-root': {
      cursor: 'default',
    },
    '& .MuiInputBase-input': {
      cursor: 'default',
    },
  };

  // --- Tab state ---
  const [activeTab, setActiveTab] = useState(0);

  // --- Overview state ---
  const [createdByEmail, setCreatedByEmail] = useState('');
  const [updatedByEmail, setUpdatedByEmail] = useState('');
  const [isLoadingEmail, setIsLoadingEmail] = useState(false);

  // --- Users tab state ---
  const [usersRows, setUsersRows] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersPage, setUsersPage] = useState(0);
  const [usersPageSize, setUsersPageSize] = useState(10);
  const [usersTotalCount, setUsersTotalCount] = useState(0);
  const [usersHasNextPage, setUsersHasNextPage] = useState(false);
  const [usersFilterModel, setUsersFilterModel] = useState({ items: [] });

  // --- User view dialog state ---
  const [viewUser, setViewUser] = useState(null);
  const [viewUserOpen, setViewUserOpen] = useState(false);
  const [viewUserLoading, setViewUserLoading] = useState(false);
  const [userSuccess, setUserSuccess] = useState('');
  const [userError, setUserError] = useState(null);

  // Reset all state when dialog closes
  useEffect(() => {
    if (!open) {
      setActiveTab(0);
      setUsersRows([]);
      setUsersTotalCount(0);
      setUsersPage(0);
      setCreatedByEmail('');
      setUpdatedByEmail('');
      setViewUser(null);
      setViewUserOpen(false);
      setUsersHasNextPage(false);
      setUsersFilterModel({ items: [] });
    }
  }, [open]);

  const activeUsersFilterItem = usersFilterModel.items.find((item) => {
    const columnField = item.columnField || item.field;
    return (columnField === 'firstName' || columnField === 'lastName' || columnField === 'email') && item.value?.trim();
  }) || null;

  const activeUsersFilterField = activeUsersFilterItem?.field || '';
  const activeUsersFilterValue = (activeUsersFilterItem?.value || '').trim();
  const activeUsersFilterOperator = activeUsersFilterItem?.operator === 'equals' ? 'equals' : 'startsWith';

  const handleUsersFilterModelChange = (nextFilterModel) => {
    const supportedFilterItem = (nextFilterModel?.items || []).find((item) => {
      const columnField = item.columnField || item.field;
      return columnField === 'firstName' || columnField === 'lastName' || columnField === 'email';
    });

    const normalizedFilterItem = supportedFilterItem
      ? {
          ...supportedFilterItem,
          operator: supportedFilterItem.operator === 'equals' ? 'equals' : 'startsWith',
          value: supportedFilterItem.value || '',
        }
      : null;

    setUsersFilterModel({
      ...nextFilterModel,
      items: normalizedFilterItem ? [normalizedFilterItem] : [],
    });
    setUsersPage(0);
  };

  // Load email metadata for the Overview tab
  useEffect(() => {
    if (!open || !hospital) return;

    const fetchEmailByUid = async (uid) => {
      if (!uid) return uid;
      const authUser = await adminService.getUser(uid).catch(() => null);
      return authUser?.email || uid;
    };

    const loadEmails = async () => {
      setIsLoadingEmail(true);
      setCreatedByEmail('');
      setUpdatedByEmail('');

      const effectiveUpdatedBy = hospital.updatedBy || hospital.createdBy;
      const uids = [hospital.createdBy, effectiveUpdatedBy].filter(Boolean);
      const uniqueUids = [...new Set(uids)];

      const resolved = await Promise.all(
        uniqueUids.map(async (uid) => [uid, await fetchEmailByUid(uid)])
      );

      const emailByUid = Object.fromEntries(resolved);

      setCreatedByEmail(emailByUid[hospital.createdBy] || hospital.createdBy || 'N/A');
      setUpdatedByEmail(emailByUid[effectiveUpdatedBy] || effectiveUpdatedBy || 'N/A');
      setIsLoadingEmail(false);
    };

    loadEmails();
  }, [open, hospital]);

  useEffect(() => {
    if (usersPage !== 0) {
      setUsersPage(0);
    }
  }, [hospital?.id, usersPageSize, activeUsersFilterField, activeUsersFilterOperator, activeUsersFilterValue]);

  const fetchHospitalUsers = useCallback(async () => {
    if (!hospital?.id) return;

    setUsersLoading(true);
    setUserError(null);

    try {
      const data = await adminService.listUsers({
        page: usersPage,
        pageSize: usersPageSize,
        hospitalId: hospital.id,
        filterField: activeUsersFilterField || undefined,
        filterOperator: activeUsersFilterOperator,
        filterValue: activeUsersFilterValue || undefined,
      });
      const hits = (data && data.users) || [];

      setUsersHasNextPage(Boolean(data?.hasMore ?? data?.nextPageToken));
      setUsersRows(
        hits.map((u) => ({
          id: u.uid,
          uid: u.uid,
          firstName: u.firstName || '',
          lastName: u.lastName || '',
          email: u.email || '',
          hospitalId: u.hospitalId || '',
          totalUploads: Number(u.totalUploads) || 0,
          lastUploadAt: u.lastUploadAt || null,
          isAdmin: u.isAdmin || false,
          isDemo: u.isDemo || false,
          isOffline: u.isOffline || false,
          mfaEnabled: u.mfaEnabled ?? false,
          totpEnrolled: u.totpEnrolled ?? false,
          emailVerified: u.emailVerified ?? false,
          avatar: u.photoURL || '',
        }))
      );
      setUsersTotalCount(typeof data?.totalCount === 'number' ? data.totalCount : -1);
    } catch (err) {
      console.error('fetchHospitalUsers failed', err);
      setUsersRows([]);
      setUsersHasNextPage(false);
      setUsersTotalCount(0);
      setUserError(err?.message || 'Failed to load hospital users');
    } finally {
      setUsersLoading(false);
    }
  }, [hospital, usersPage, usersPageSize, activeUsersFilterField, activeUsersFilterOperator, activeUsersFilterValue]);

  useEffect(() => {
    if (activeTab === 1 && open && hospital) {
      fetchHospitalUsers();
    }
  }, [activeTab, open, hospital, usersPage, fetchHospitalUsers]);


  const handleRowView = async (user) => {
    const uid = user.uid || user.id;
    if (!uid) return;

    setViewUserLoading(true);
    try {
      const full = await adminService.getUser(uid);
      let hospitalName = '';

      if (full?.hospitalId) {
        const hospitalDetail = await getHospital(full.hospitalId).catch(() => null);
        hospitalName = hospitalDetail?.name || '';
      }

      const createdBy = await resolveUidToEmail(adminService, full?.createdBy);
      const updatedBy = await resolveUidToEmail(adminService, full?.updatedBy);

      setViewUser({
        ...full,
        hospitalName,
        createdBy,
        updatedBy,
      });
      setViewUserOpen(true);
    } catch (err) {
      console.error('getUser for view failed', err);
    } finally {
      setViewUserLoading(false);
    }
  };

  const handleRowUpdate = (user) => {
    setUserError(null);
    setUserSuccess('');
    return adminService
      .updateUser({ uid: user.uid || user.id, ...user })
      .then(() => {
        setUserSuccess('User updated successfully');
        fetchHospitalUsers();
      })
      .catch((err) => {
        setUserError(err?.message || 'Failed to update user');
        return Promise.reject(err);
      });
  };

  const handleRowDelete = (user) => {
    setUserError(null);
    setUserSuccess('');
    return adminService
      .deleteUser(user.uid || user.id)
      .then(() => {
        setUserSuccess('User deleted successfully');
        fetchHospitalUsers();
      })
      .catch((err) => {
        setUserError(err?.message || 'Failed to delete user');
        return Promise.reject(err);
      });
  };

  const handleRowReset = (user) => {
    if (!user || !user.email) return Promise.resolve();
    setUserError(null);
    setUserSuccess('');
    return sendPasswordResetEmail(auth, user.email)
      .then(() => {
        setUserSuccess(`Password reset email sent to ${user.email}`);
      })
      .catch((err) => {
        console.error('sendPasswordResetEmail failed', err);
        setUserError(err?.message || 'Failed to send password reset email');
        return Promise.reject(err);
      });
  };

  useEffect(() => {
    if (!userSuccess) return;
    const timer = setTimeout(() => setUserSuccess(''), 5000);
    return () => clearTimeout(timer);
  }, [userSuccess]);

  useEffect(() => {
    if (!userError) return;
    const timer = setTimeout(() => setUserError(null), 5000);
    return () => clearTimeout(timer);
  }, [userError]);

  useEffect(() => {
    if (!open) {
      setUserError(null);
      setUserSuccess('');
    }
  }, [open]);

  const handleRowAdd = (user) => {
    setUserError(null);
    setUserSuccess('');
    return adminService
      .createUser(user)
      .then(() => {
        setUserSuccess('User created successfully');
        fetchHospitalUsers();
      })
      .catch((err) => {
        setUserError(err?.message || 'Failed to create user');
        return Promise.reject(err);
      });
  };



  const currentMonthKey = new Date().toISOString().slice(0, 7);
  const totalUploads = Number(hospital?.uploadStats?.totalUploads) || 0;
  const uploadsThisMonth = Number(hospital?.uploadStats?.uploadsByMonth?.[currentMonthKey]) || 0;

  if (!hospital) return null;

  const handleDialogClose = () => {
    setUserError(null);
    setUserSuccess('');
    onClose && onClose();
  };

  return (
    <>
      <Dialog open={!!open} onClose={handleDialogClose} maxWidth="md" fullWidth disableScrollLock>
        <DialogTitle>{hospital.name || 'Hospital details'}</DialogTitle>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
            <Tab label="Overview" />
            <Tab label="Users" />
          </Tabs>
        </Box>
        {activeTab === 1 && userError && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5, mb: 0.25, width: '100%', '& .MuiAlert-root': { mb: 0 } }}>
            <Box sx={{ maxWidth: 480, width: '100%' }}>
              <NotificationAlert error={userError} onClear={() => setUserError(null)} />
            </Box>
          </Box>
        )}
        {activeTab === 1 && userSuccess && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5, mb: 0.25, width: '100%', '& .MuiAlert-root': { mb: 0 } }}>
            <Box sx={{ maxWidth: 480, width: '100%' }}>
              <NotificationAlert severity="success" message={userSuccess} duration={5000} onClear={() => setUserSuccess('')} />
            </Box>
          </Box>
        )}
        <DialogContent sx={{ pt: 0.5 }}>

          {/* Overview Tab — existing content unchanged */}
          {activeTab === 0 && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, width: '100%' }}>
                <Avatar sx={{ width: 80, height: 80, fontSize: 32 }}>
                  <LocalHospitalIcon fontSize="large" />
                </Avatar>
              </Box>
              <TextField
                margin="normal"
                label="Name"
                value={hospital.name || ''}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={disabledSx}
              />
              <TextField
                margin="normal"
                label="Address"
                value={hospital.address || ''}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={disabledSx}
              />
              <TextField
                margin="normal"
                label="Country"
                value={hospital.country || ''}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={disabledSx}
              />
              <TextField
                margin="normal"
                label="Contact Email"
                value={hospital.contactEmail || ''}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={disabledSx}
              />
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1">Upload Summary</Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                  gap: 2,
                  mt: 1.5,
                }}
              >
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Total Uploads
                      </Typography>
                      <FileUploadOutlinedIcon color="primary" fontSize="small" />
                    </Box>
                    <Typography variant="h4" component="div">
                      {totalUploads}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      All successful uploads recorded for this hospital.
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                       Monthly Uploads
                      </Typography>
                      <CalendarMonthOutlinedIcon color="primary" fontSize="small" />
                    </Box>
                    <Typography variant="h4" component="div">
                      {uploadsThisMonth}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Uploads for the month: {currentMonthKey}.
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1">Metadata</Typography>
              <TextField
                margin="normal"
                label="Hospital ID"
                value={hospital.id || 'N/A'}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={disabledSx}
              />
              <TextField
                margin="normal"
                label="Created by"
                value={isLoadingEmail ? 'Loading...' : createdByEmail || hospital.createdBy || 'N/A'}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={disabledSx}
              />
              <TextField
                margin="normal"
                label="Created at"
                value={formatTimestamp(hospital.createdAt)}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={disabledSx}
              />
              <TextField
                margin="normal"
                label="Updated by"
                value={isLoadingEmail ? 'Loading...' : updatedByEmail || hospital.updatedBy || hospital.createdBy || 'N/A'}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={disabledSx}
              />
              <TextField
                margin="normal"
                label="Updated at"
                value={formatTimestamp(hospital.updatedAt)}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={disabledSx}
              />
              {loading && <Typography sx={{ mt: 1 }}>Loading details...</Typography>}
            </>
          )}

          {/* Users Tab — scoped to this hospital */}
          {activeTab === 1 && (
            <UsersDataGrid
              rows={usersRows}
              loading={usersLoading || viewUserLoading}
              page={usersPage}
              pageSize={usersPageSize}
              rowCount={usersTotalCount}
              hasMore={usersHasNextPage}
              filterModel={usersFilterModel}
              onFilterModelChange={handleUsersFilterModelChange}
              onPageChange={(p) => setUsersPage(p)}
              onPageSizeChange={(ps) => setUsersPageSize(ps)}
              onRowView={handleRowView}
              onRowAdd={handleRowAdd}
              onRowUpdate={handleRowUpdate}
              onRowDelete={handleRowDelete}
              onRowReset={handleRowReset}
              defaultHospitalId={hospital?.id || ''}
              defaultHospitalName={hospital?.name || ''}
              fixedHospital
              embedded
            />
          )}

        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* User detail dialog opened from the Users tab */}
      <UserViewDialog
        open={viewUserOpen}
        user={viewUser}
        onClose={() => setViewUserOpen(false)}
        onUserUpdated={() => fetchHospitalUsers()}
        onRefreshUser={(uid) => {
          adminService.getUser(uid).then((full) => {
            if (!full) return;
            setViewUser((prev) => ({ ...prev, ...full, hospitalName: prev?.hospitalName || '' }));
          }).catch(() => {});
        }}
      />
    </>
  );
}

